// ink-component
import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';
import getGlobalProps from '../../helpers/constants/global-props';
import Icon from '../Icon/Icon';
import Text from '../Typography/Text/Text';
import Heading from '../Typography/Heading/Heading';
import Button from '../Button/Button';
import Dialog from '../Dialog/Dialog';
import {
  FullPageModalWrapper,
  FullPageModalHeader,
  FullPageModalHeaderActions,
  FullPageModalHeaderActionsWrapper,
  FullPageModalHeaderClose,
  FullPageModalContent,
  FullPageModalContentChildren,
  FullPageModalBackdrop,
} from './styles';

export interface FullPageModalProps {
  /** Bold text of the header */
  title?: ReactNode;
  /** Smaller, secondary text after the title */
  text?: ReactNode;
  /** Contents of the FullPageModal */
  children?: ReactNode;
  /** Removes built-in spacing */
  trim?: boolean;
  /** Header icon right callback */
  iconRightCallback?: () => void;
  /** Actions in the top right of the header */
  actions?: ReactNode;
  /** Pass a collapsible sidebar component, like [Slider from @carta/spreadsheet-v1](https://github.com/carta/carta-frontend-platform/blob/master/libs/design-technology/spreadsheet-v1/library/Slider/Slider.md). */
  slider?: () => React.ReactElement;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;

  /** @ignore */
  variant?: 'default' | 'workflow';
  /** @ignore */
  hideModal?: () => void;

  /** this needs to be here because there are several files in the codebase using this
   * prop: https://github.com/search?q=org%3Acarta%20iconRightType&type=code */
  /** @ignore */
  iconRightType?: string;
}

interface ContextType {
  fullPageModalScrollRef?: React.RefObject<HTMLDivElement>;
}

export const FullPageModalContext = React.createContext<ContextType>({
  fullPageModalScrollRef: undefined,
});

const globalProps = getGlobalProps({});

/**
 * @purpose Opens a full page overlay to switch context to specific operations
 * @group Modal
 * @categories layout
 */
function FullPageModal(props: FullPageModalProps) {
  const fullPageModalScrollRef = React.useRef(null);
  const {
    title,
    text,
    iconRightCallback,
    slider,
    children,
    trim = false,
    actions,
    'data-testid': dataTestId,
    variant,
    ...otherProps
  } = props;
  const Slider = slider;

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        fullPageModalSlider: !!slider,
        fullPageModalTrim: trim,
      })}
    >
      <FullPageModalContext.Provider value={{ fullPageModalScrollRef }}>
        <Dialog>
          <FullPageModalWrapper {...globalProps} {...otherProps} data-testid={props['data-testid']}>
            <FullPageModalHeader>
              {title && (
                <div>
                  <Heading variant="heading-3" trim>
                    {title}
                  </Heading>
                  {title && text && (
                    <Text variant="body-3-feedback-neutral" trim>
                      {text}
                    </Text>
                  )}
                </div>
              )}
              <FullPageModalHeaderActionsWrapper>
                <FullPageModalHeaderActions>{actions}</FullPageModalHeaderActions>
                {iconRightCallback && (
                  <FullPageModalHeaderClose>
                    {variant === 'workflow' ? (
                      <Button type="transparent" onClick={iconRightCallback} title="exit modal">
                        Exit
                      </Button>
                    ) : (
                      <Button type="aura" onClick={iconRightCallback}>
                        <Icon type="close" size="xxl" meaning="close modal" />
                      </Button>
                    )}
                  </FullPageModalHeaderClose>
                )}
              </FullPageModalHeaderActionsWrapper>
            </FullPageModalHeader>
            <FullPageModalContent>
              <FullPageModalContentChildren
                ref={fullPageModalScrollRef}
                data-testid={dataTestId ? `${dataTestId}-content` : undefined}
              >
                {children}
              </FullPageModalContentChildren>
              {Slider && <Slider />}
            </FullPageModalContent>
            <FullPageModalBackdrop />
          </FullPageModalWrapper>
        </Dialog>
      </FullPageModalContext.Provider>
    </ThemeProvider>
  );
}

FullPageModal.defaultProps = {
  title: undefined,
  text: undefined,
  children: undefined,
  iconRightCallback: undefined,
  actions: undefined,
  slider: undefined,
  'data-testid': undefined,
  variant: 'default',
};

FullPageModal.displayName = 'FullPageModal';

export default FullPageModal;
